/**
 * Landmark curation.
 *
 * The job here is one question per landmark: positive, negative, or neutral in THIS
 * neighbourhood? That is not a property of the landmark. Bedford Av and Grand St are both
 * L stops in Williamsburg and they measure opposite -- +11.2% within 200m against -10.7% --
 * because Grand St does not make rent lower, it sits where rent is lower.
 *
 * So the page never asks anyone to invent a sign. Pick a landmark and it fits the rent
 * gradient from that hood's own realized leases, shows the bands, and suggests a type to
 * confirm or override. "Neutral" then means "measured, and it does not move rent here"
 * rather than "nobody got round to it".
 *
 * Geometry is not editable. Parks and the shoreline are imported from NYC Open Data with
 * real boundaries and refreshed on re-import; what a person owns is the judgement about
 * them, plus adding the point landmarks no dataset carries -- a power plant, a particular
 * project, a loud bar strip.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { GoogleMap, Marker, Polygon, Polyline, useLoadScript } from "@react-google-maps/api";
import { authFetch } from "../api";

import NeighborhoodSelect from "../components/NeighborhoodSelect";

/**
 * authFetch resolves on EVERY status except 401 — it returns res.json() regardless — so a
 * 404 or a 400 arrives as a perfectly ordinary object like { detail: "Not Found" }. Every
 * try/catch on this page was therefore dead code, and the first symptom was a crash:
 * `queue.candidates[0]` on a 404 body, "Cannot read properties of undefined".
 *
 * FastAPI puts its reason in `detail`, and this API refuses several things deliberately —
 * moving an imported landmark, flagging a highway — so that text is worth showing rather
 * than discarding. Throwing here makes the existing catch blocks do what they always
 * claimed to.
 */
async function api(url, options) {
  const body = await authFetch(url, options);
  if (body && typeof body === "object" && body.detail && !Array.isArray(body)) {
    const msg = typeof body.detail === "string" ? body.detail : JSON.stringify(body.detail);
    throw new Error(msg);
  }
  return body;
}

const TYPE_COLORS = {
  positive: "#2e7d32",
  negative: "#c62828",
  neutral: "#757575",
};

const TYPE_LABEL = {
  positive: "Positive",
  negative: "Negative",
  neutral: "Neutral",
};

function Bar({ pct }) {
  // One shared scale across every band so bars stay comparable between landmarks.
  const max = 25;
  if (pct === null || pct === undefined) {
    return <span style={{ color: "#999", fontSize: 12 }}>too few leases</span>;
  }
  const width = Math.min(Math.abs(pct) / max, 1) * 100;
  const positive = pct >= 0;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <div style={{ position: "relative", width: 160, height: 14, background: "#f0f0f0", borderRadius: 3 }}>
        <div
          style={{
            position: "absolute",
            left: positive ? "50%" : `${50 - width / 2}%`,
            width: `${width / 2}%`,
            height: "100%",
            background: positive ? "#2e7d32" : "#c62828",
            borderRadius: 3,
          }}
        />
        <div style={{ position: "absolute", left: "50%", top: -2, width: 1, height: 18, background: "#bbb" }} />
      </div>
      <span style={{ fontVariantNumeric: "tabular-nums", fontSize: 13, minWidth: 52 }}>
        {pct > 0 ? "+" : ""}{pct}%
      </span>
    </div>
  );
}

export default function LandmarksPage() {
  const [hoods, setHoods] = useState([]);
  const [hoodsError, setHoodsError] = useState(null);
  const [hoodId, setHoodId] = useState(null);
  const [landmarks, setLandmarks] = useState([]);
  const [counts, setCounts] = useState({});
  const [hiddenCount, setHiddenCount] = useState(0);
  const [showOutOfReach, setShowOutOfReach] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [impact, setImpact] = useState(null);
  const [impactLoading, setImpactLoading] = useState(false);
  const [filter, setFilter] = useState({ type: "all", source: "all", q: "" });
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [adding, setAdding] = useState(false);
  const [newLm, setNewLm] = useState({ landmark_name: "", latitude: "", longitude: "", description: "" });
  const [edit, setEdit] = useState(null);   // { landmark_name, weight, description } or null
  // Click-to-trace, lifted from ZonesPage: the Maps JS DrawingManager was removed in API
  // 3.65, so a shape is built from map clicks.
  const [queue, setQueue] = useState(null);         // { flag, candidates, ... } or null
  const [queueIdx, setQueueIdx] = useState(0);
  const [tracing, setTracing] = useState(null);      // null | "line" | "polygon"
  const [drawPath, setDrawPath] = useState([]);

  useEffect(() => {
    // /buildings/neighborhoods, not /neighborhoods: the latter returns stats keyed by
    // neighborhood NAME with no id at all, so every option rendered blank and hoodId was
    // never set. Its own docstring points here for dropdowns.
    api("/buildings/neighborhoods")
      .then((d) => {
        const list = Array.isArray(d?.neighborhoods) ? d.neighborhoods : [];
        setHoods(list);
        if (!list.length) {
          // An empty picker with no explanation is indistinguishable from a broken page.
          setHoodsError(`The server returned ${JSON.stringify(d).slice(0, 120)}`);
          return;
        }
        setHoodsError(null);
        // Default to the hood we have landmark data for rather than whatever sorts first.
        const wb = list.find((n) => n.name === "Williamsburg");
        if (hoodId === null) setHoodId((wb || list[0]).id);
      })
      .catch((e) => setHoodsError(e?.message || String(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = useCallback(async () => {
    if (!hoodId) return;
    setLoading(true);
    try {
      const d = await api(
        `/landmarks?neighborhood_id=${hoodId}&include_out_of_reach=${showOutOfReach}`);
      setLandmarks(Array.isArray(d?.landmarks) ? d.landmarks : []);
      setCounts(d.counts || {});
      setHiddenCount(d.hidden_out_of_reach || 0);
    } catch {
      setToast("Could not load landmarks");
    } finally {
      setLoading(false);
    }
  }, [hoodId, showOutOfReach]);

  useEffect(() => {
    load();
    setSelected(null);
    setImpact(null);
  }, [load]);

  const shown = useMemo(() => {
    const q = filter.q.trim().toLowerCase();
    return landmarks.filter((l) => {
      if (filter.type !== "all" && l.landmark_type !== filter.type) return false;
      if (filter.source === "imported" && !l.is_imported) return false;
      if (filter.source === "manual" && l.is_imported) return false;
      if (q && !l.landmark_name.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [landmarks, filter]);

  async function select(lm) {
    setSelected(lm);
    setEdit(null);
    setImpact(null);
    setImpactLoading(true);
    try {
      setImpact(await api(`/landmarks/${lm.id}/impact?neighborhood_id=${hoodId}`));
    } catch {
      setToast("Could not measure this landmark");
    } finally {
      setImpactLoading(false);
    }
  }

  async function setType(lm, type) {
    setSaving(true);
    try {
      await api(`/landmarks/${lm.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ landmark_type: type }),
      });
      setLandmarks((prev) => prev.map((x) => (x.id === lm.id ? { ...x, landmark_type: type } : x)));
      setSelected((s) => (s && s.id === lm.id ? { ...s, landmark_type: type } : s));
      setCounts((c) => ({ ...c, [type]: (c[type] || 0) + 1, [lm.landmark_type]: Math.max(0, (c[lm.landmark_type] || 1) - 1) }));
      setToast(`${lm.landmark_name} → ${TYPE_LABEL[type]}`);
    } catch {
      setToast("Could not save");
    } finally {
      setSaving(false);
    }
  }

  async function openQueue(lm) {
    setSaving(true);
    try {
      const d = await api(`/landmarks/${lm.id}/candidates?neighborhood_id=${hoodId}`);
      if (!d || !Array.isArray(d.candidates)) throw new Error("Unexpected response from the server");
      setQueue(d);
      // Resume where the reviewing stopped rather than at the top.
      const firstUnreviewed = d.candidates.findIndex((c) => !c.reviewed);
      setQueueIdx(firstUnreviewed >= 0 ? firstUnreviewed : 0);
    } catch (err) {
      setToast(err?.message || "No building review for this landmark");
    } finally {
      setSaving(false);
    }
  }

  async function answerQueue(yes) {
    const c = queue.candidates[queueIdx];
    const next = yes
      ? Array.from(new Set([...(c.flags || []), queue.flag]))
      : (c.flags || []).filter((f) => f !== queue.flag);
    try {
      await api(`/landmarks/buildings/${c.property_id}/flags`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ flags: next }),
      });
      setQueue((q) => {
        const cs = [...q.candidates];
        cs[queueIdx] = { ...c, flags: next, has_flag: yes, reviewed: true };
        return { ...q, candidates: cs, reviewed: q.reviewed + (c.reviewed ? 0 : 1) };
      });
      setQueueIdx((i) => Math.min(i + 1, queue.candidates.length - 1));
    } catch (err) {
      setToast(err?.message || "Could not save");
    }
  }

  async function patchLandmark(lm, body, msg) {
    setSaving(true);
    try {
      const r = await api(`/landmarks/${lm.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      setLandmarks((prev) => prev.map((x) => (x.id === lm.id ? { ...x, ...body, ...r } : x)));
      setSelected((sel) => (sel && sel.id === lm.id ? { ...sel, ...body, ...r } : sel));
      if (msg) setToast(msg);
      return true;
    } catch (err) {
      // The API refuses some edits on purpose (moving an imported landmark, for one) and
      // says why. Surface its reason rather than a generic failure.
      setToast(err?.message || "Could not save");
      return false;
    } finally {
      setSaving(false);
    }
  }

  async function removeLandmark(lm) {
    if (!window.confirm(`Delete "${lm.landmark_name}"? This cannot be undone.`)) return;
    setSaving(true);
    try {
      await api(`/landmarks/${lm.id}`, { method: "DELETE" });
      setSelected(null);
      setImpact(null);
      setToast(`Deleted ${lm.landmark_name}`);
      load();
    } catch (err) {
      setToast(err?.message || "Could not delete");
    } finally {
      setSaving(false);
    }
  }

  async function addLandmark(e) {
    e.preventDefault();
    const lat = parseFloat(newLm.latitude);
    const lng = parseFloat(newLm.longitude);
    if (!newLm.landmark_name.trim() || Number.isNaN(lat) || Number.isNaN(lng)) {
      setToast("Name, latitude and longitude are all required");
      return;
    }
    setSaving(true);
    try {
      await api("/landmarks", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          neighborhood_id: hoodId,
          landmark_name: newLm.landmark_name.trim(),
          // Always neutral on creation: it has not been measured yet, and claiming a sign
          // before measuring is the habit this page exists to break.
          landmark_type: "neutral",
          latitude: lat,
          longitude: lng,
          description: newLm.description || null,
        }),
      });
      setNewLm({ landmark_name: "", latitude: "", longitude: "", description: "" });
      setAdding(false);
      setToast("Added as neutral — measure it, then set the type");
      load();
    } catch {
      setToast("Could not add landmark");
    } finally {
      setSaving(false);
    }
  }

  const agrees = impact && impact.suggested_type && impact.suggested_type === impact.current_type;

  const { isLoaded: mapsReady } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
  });

  // Centre on the landmarks being shown rather than a hard-coded point, so switching
  // neighbourhood moves the map without anyone maintaining a table of centres.
  const center = useMemo(() => {
    const pts = shown.filter((l) => l.lat && l.lng);
    if (!pts.length) return { lat: 40.7191, lng: -73.9573 };
    return {
      lat: pts.reduce((a, l) => a + l.lat, 0) / pts.length,
      lng: pts.reduce((a, l) => a + l.lng, 0) / pts.length,
    };
  }, [shown]);

  // Geometry is a list of RINGS. McCarren Park is five disconnected parts, and drawing
  // them as one path produced a bowtie across the map. Google's Polygon takes an array of
  // paths natively; Polyline takes one, so a multi-part line renders as several.
  const rings = (geometry) => {
    if (!geometry || !geometry.length) return [];
    const nested = Array.isArray(geometry[0]) && Array.isArray(geometry[0][0]);
    const list = nested ? geometry : [geometry];       // tolerate legacy flat geometry
    return list.map((r) => r.map(([lat, lng]) => ({ lat, lng })));
  };

  function onMapClick(e) {
    if (!e.latLng) return;
    if (tracing) {
      setDrawPath((prev) => [...prev, { lat: e.latLng.lat(), lng: e.latLng.lng() }]);
      return;
    }
    if (!adding) return;
    // Click-to-place: typing coordinates is not a real answer to "where is the substation".
    setNewLm((n) => ({
      ...n,
      latitude: e.latLng.lat().toFixed(6),
      longitude: e.latLng.lng().toFixed(6),
    }));
    setToast("Point placed — now name it and save");
  }

  async function saveTraced() {
    const name = window.prompt(
      `Name this ${tracing} (e.g. "BQE — Williamsburg section")`);
    if (!name || !name.trim()) return;
    setSaving(true);
    try {
      const r = await api("/landmarks", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          neighborhood_id: hoodId,
          landmark_name: name.trim(),
          landmark_type: "neutral",     // measure before claiming a sign
          shape: tracing,
          geometry: drawPath.map((p) => [p.lat, p.lng]),
        }),
      });
      setToast(`Saved as neutral — ${r.vertices} points. Measure it, then set the type.`);
      setTracing(null);
      setDrawPath([]);
      load();
    } catch (err) {
      setToast(err?.message || "Could not save the shape");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ padding: 24, maxWidth: 1180, margin: "0 auto", fontFamily: "system-ui, sans-serif" }}>
      <h1 style={{ margin: "0 0 4px" }}>Landmarks</h1>
      <p style={{ margin: "0 0 20px", color: "#666", fontSize: 14, maxWidth: 760 }}>
        Each landmark is positive, negative or neutral <em>in this neighbourhood</em>. Pick one
        and its rent gradient is measured from this hood&rsquo;s own closed leases — confirm the
        suggestion or override it.
      </p>

      {hoodsError && (
        <div style={{ marginBottom: 14, padding: 10, borderRadius: 6, background: "#fff4f4",
                      border: "1px solid #f3c6c6", color: "#b23", fontSize: 13 }}>
          <strong>Could not load neighbourhoods.</strong> {hoodsError}
          {" "}If this says Unauthorized, log in again.
        </div>
      )}

      <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", marginBottom: 16 }}>
        <div style={{ minWidth: 240 }} title={`${hoods.length} neighbourhoods loaded`}>
          <NeighborhoodSelect
            neighborhoods={hoods}
            selected={hoodId}
            onChange={(id) => setHoodId(Number(id))}
            multi={false}
            label=""
            placeholder="Select a neighborhood"
          />
        </div>
        <select value={filter.type} onChange={(e) => setFilter({ ...filter, type: e.target.value })} style={{ padding: 8 }}>
          <option value="all">All types ({landmarks.length})</option>
          <option value="positive">Positive ({counts.positive || 0})</option>
          <option value="negative">Negative ({counts.negative || 0})</option>
          <option value="neutral">Neutral ({counts.neutral || 0})</option>
        </select>
        <select value={filter.source} onChange={(e) => setFilter({ ...filter, source: e.target.value })} style={{ padding: 8 }}>
          <option value="all">Any source</option>
          <option value="imported">Imported</option>
          <option value="manual">Added by hand</option>
        </select>
        <input placeholder="Search…" value={filter.q}
               onChange={(e) => setFilter({ ...filter, q: e.target.value })}
               style={{ padding: 8, flex: "1 1 180px", minWidth: 140 }} />
        <button onClick={() => { setAdding((a) => !a); setTracing(null); setDrawPath([]); }}
                style={{ padding: "8px 14px" }}>
          {adding ? "Cancel" : "+ Add point"}
        </button>
        {!adding && !tracing && (
          <>
            <button onClick={() => { setTracing("line"); setDrawPath([]); }} style={{ padding: "8px 14px" }}>
              + Trace line
            </button>
            <button onClick={() => { setTracing("polygon"); setDrawPath([]); }} style={{ padding: "8px 14px" }}>
              + Trace area
            </button>
          </>
        )}
        {hiddenCount > 0 && (
          <label style={{ fontSize: 13, color: "#777", display: "flex", alignItems: "center", gap: 6 }}>
            <input type="checkbox" checked={showOutOfReach}
                   onChange={(e) => setShowOutOfReach(e.target.checked)} />
            show {hiddenCount} too far to matter
          </label>
        )}
      </div>

      {adding && (
        <form onSubmit={addLandmark}
              style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16, padding: 12, background: "#fafafa", border: "1px solid #eee", borderRadius: 6 }}>
          <input placeholder="Name (e.g. Kent Ave substation)" value={newLm.landmark_name}
                 onChange={(e) => setNewLm({ ...newLm, landmark_name: e.target.value })}
                 style={{ padding: 8, flex: "2 1 240px" }} />
          <input placeholder="Click the map ↑" value={newLm.latitude} readOnly
                 style={{ padding: 8, flex: "1 1 110px", background: "#f5f5f5", color: "#555" }} />
          <input placeholder="Click the map ↑" value={newLm.longitude} readOnly
                 style={{ padding: 8, flex: "1 1 110px", background: "#f5f5f5", color: "#555" }} />
          <input placeholder="Note (optional)" value={newLm.description}
                 onChange={(e) => setNewLm({ ...newLm, description: e.target.value })} style={{ padding: 8, flex: "2 1 200px" }} />
          <button type="submit" disabled={saving} style={{ padding: "8px 16px" }}>Add</button>
          <div style={{ flexBasis: "100%", color: "#777", fontSize: 12 }}>
            Click the map to place it. Saved as <strong>neutral</strong> — measure it first, then set the type.
          </div>
        </form>
      )}

      {queue && Array.isArray(queue.candidates) && (() => {
        const c = queue.candidates[queueIdx];
        if (!c) return null;
        const label = { on_park: "on the park", waterfront: "on the water" }[queue.flag] || queue.flag;
        return (
          <div style={{ marginBottom: 18, padding: 16, border: "2px solid #1565c0", borderRadius: 6, background: "#f5f9ff" }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
              <strong style={{ fontSize: 17 }}>Is this building {label}?</strong>
              <span style={{ color: "#666", fontSize: 13 }}>
                {queue.landmark_name} · {queue.reviewed} of {queue.total} reviewed
              </span>
              <button onClick={() => setQueue(null)} style={{ marginLeft: "auto", padding: "4px 10px" }}>Close</button>
            </div>
            <div style={{ margin: "10px 0", fontSize: 20 }}>{c.address}</div>
            <div style={{ color: "#666", fontSize: 13, marginBottom: 12 }}>
              {c.distance_m}m away · {c.rentals} rentals
              {c.reviewed && <span style={{ color: "#2e7d32" }}> · already answered: {c.has_flag ? "yes" : "no"}</span>}
              {" · "}
              <a href={`https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${c.lat},${c.lng}`}
                 target="_blank" rel="noreferrer">Street View</a>
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <button onClick={() => answerQueue(true)}
                      style={{ padding: "9px 22px", background: "#2e7d32", color: "#fff", border: "none", borderRadius: 5 }}>
                Yes
              </button>
              <button onClick={() => answerQueue(false)}
                      style={{ padding: "9px 22px", background: "#fff", border: "1px solid #999", borderRadius: 5 }}>
                No
              </button>
              <button onClick={() => setQueueIdx((i) => Math.min(i + 1, queue.candidates.length - 1))}
                      style={{ padding: "9px 14px" }}>Skip</button>
              <span style={{ marginLeft: "auto", color: "#888", fontSize: 13 }}>
                {queueIdx + 1} / {queue.candidates.length}
              </span>
            </div>
            <p style={{ margin: "10px 0 0", color: "#777", fontSize: 12 }}>
              Distance only says it <em>could</em> be — a building 50m away facing the other
              way has no park. Street View settles it.
            </p>
          </div>
        );
      })()}

      <div style={{ marginBottom: 18, border: "1px solid #eee", borderRadius: 6, overflow: "hidden", position: "relative" }}>
        {!mapsReady && <div style={{ padding: 16, color: "#666" }}>Loading map…</div>}
        {mapsReady && (
          <GoogleMap
            mapContainerStyle={{ width: "100%", height: 380 }}
            center={center}
            zoom={14}
            onClick={onMapClick}
            options={{ streetViewControl: false, mapTypeControl: true,
                       draggableCursor: (adding || tracing) ? "crosshair" : undefined }}
          >
            {shown.map((lm) => {
              const color = TYPE_COLORS[lm.landmark_type];
              const chosen = selected && selected.id === lm.id;
              if (lm.shape === "polygon" && lm.geometry) {
                return <Polygon key={lm.id} paths={rings(lm.geometry)} onClick={() => select(lm)}
                  options={{ strokeColor: color, strokeWeight: chosen ? 3 : 1.5,
                             fillColor: color, fillOpacity: chosen ? 0.35 : 0.15 }} />;
              }
              if (lm.shape === "line" && lm.geometry) {
                return rings(lm.geometry).map((r, i) => (
                  <Polyline key={`${lm.id}-${i}`} path={r} onClick={() => select(lm)}
                    options={{ strokeColor: color, strokeWeight: chosen ? 5 : 3, strokeOpacity: 0.8 }} />
                ));
              }
              const movable = !lm.is_imported && lm.shape === "point";
              return <Marker key={lm.id} position={{ lat: lm.lat, lng: lm.lng }}
                title={movable ? `${lm.landmark_name} (drag to move)` : lm.landmark_name}
                draggable={movable}
                onDragEnd={(e) => patchLandmark(lm,
                  { latitude: +e.latLng.lat().toFixed(6), longitude: +e.latLng.lng().toFixed(6) },
                  `Moved ${lm.landmark_name}`)}
                onClick={() => select(lm)}
                icon={{ path: window.google.maps.SymbolPath.CIRCLE,
                        scale: chosen ? 9 : 6, fillColor: color, fillOpacity: 0.9,
                        strokeColor: "#fff", strokeWeight: 2 }} />;
            })}
            {tracing === "polygon" && drawPath.length > 0 && (
              <Polygon paths={[drawPath]} options={{ strokeColor: "#1565c0", strokeWeight: 2,
                                                     fillColor: "#1565c0", fillOpacity: 0.2 }} />
            )}
            {tracing === "line" && drawPath.length > 1 && (
              <Polyline path={drawPath} options={{ strokeColor: "#1565c0", strokeWeight: 4 }} />
            )}
            {tracing && drawPath.map((p, i) => (
              <Marker key={`v${i}`} position={p}
                      icon={{ path: window.google.maps.SymbolPath.CIRCLE, scale: 4,
                              fillColor: "#1565c0", fillOpacity: 1, strokeColor: "#fff", strokeWeight: 1 }} />
            ))}
            {queue && Array.isArray(queue.candidates) && queue.candidates[queueIdx] && (
              <Marker position={{ lat: queue.candidates[queueIdx].lat, lng: queue.candidates[queueIdx].lng }}
                      icon={{ path: window.google.maps.SymbolPath.CIRCLE, scale: 11,
                              fillColor: "#1565c0", fillOpacity: 0.85, strokeColor: "#fff", strokeWeight: 3 }} />
            )}
            {adding && newLm.latitude && newLm.longitude && (
              <Marker position={{ lat: parseFloat(newLm.latitude), lng: parseFloat(newLm.longitude) }}
                      icon={{ path: window.google.maps.SymbolPath.BACKWARD_CLOSED_ARROW,
                              scale: 6, fillColor: "#1565c0", fillOpacity: 1,
                              strokeColor: "#fff", strokeWeight: 2 }} />
            )}
          </GoogleMap>
        )}
        {adding && !tracing && (
          <div style={{ position: "absolute", top: 10, left: 10, background: "#1565c0", color: "#fff",
                        padding: "6px 12px", borderRadius: 4, fontSize: 13, pointerEvents: "none" }}>
            Click the map to place the point
          </div>
        )}
        {tracing && (
          <div style={{ position: "absolute", top: 10, left: 10, display: "flex", gap: 8, alignItems: "center",
                        background: "#1565c0", color: "#fff", padding: "6px 10px", borderRadius: 4, fontSize: 13 }}>
            <span>Click to add points — {drawPath.length} so far</span>
            <button disabled={drawPath.length < (tracing === "polygon" ? 3 : 2) || saving}
                    onClick={saveTraced} style={{ padding: "3px 10px" }}>Finish</button>
            <button disabled={!drawPath.length}
                    onClick={() => setDrawPath((p) => p.slice(0, -1))} style={{ padding: "3px 10px" }}>Undo</button>
            <button onClick={() => { setTracing(null); setDrawPath([]); }} style={{ padding: "3px 10px" }}>Cancel</button>
          </div>
        )}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "minmax(320px, 1fr) minmax(360px, 1.1fr)", gap: 20, alignItems: "start" }}>
        <div style={{ border: "1px solid #eee", borderRadius: 6, maxHeight: 620, overflowY: "auto" }}>
          {loading && <div style={{ padding: 16, color: "#666" }}>Loading…</div>}
          {!loading && shown.length === 0 && <div style={{ padding: 16, color: "#666" }}>No landmarks match.</div>}
          {shown.map((lm) => (
            <button key={lm.id} onClick={() => select(lm)}
                    style={{
                      display: "block", width: "100%", textAlign: "left", padding: "10px 12px",
                      border: "none", borderBottom: "1px solid #f2f2f2", cursor: "pointer",
                      background: selected && selected.id === lm.id ? "#eef5ff" : "#fff",
                    }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ width: 8, height: 8, borderRadius: "50%", background: TYPE_COLORS[lm.landmark_type], flexShrink: 0 }} />
                <span style={{ fontWeight: 500, flex: 1 }}>{lm.landmark_name}</span>
                <span style={{ fontSize: 11, color: "#999" }}>{lm.shape}</span>
              </div>
              <div style={{ fontSize: 12, color: "#888", marginLeft: 16 }}>
                {TYPE_LABEL[lm.landmark_type]}
                {" · "}
                <span style={{ color: lm.buildings_in_reach === 0 ? "#c62828" : "#888" }}>
                  {lm.buildings_in_reach.toLocaleString()} buildings within 1.2km
                </span>
                {lm.is_imported ? ` · ${lm.source}` : " · added by hand"}
                {lm.description ? ` · ${lm.description}` : ""}
              </div>
            </button>
          ))}
        </div>

        <div style={{ border: "1px solid #eee", borderRadius: 6, padding: 16, minHeight: 240 }}>
          {!selected && (
            <div style={{ color: "#666", lineHeight: 1.6 }}>
              <strong>Pick a landmark on the map or in the list.</strong>
              <p style={{ marginTop: 8 }}>
                Measuring takes every closed lease in this neighbourhood, strips out what
                size, bedrooms, floor and renovation explain, and asks what rent is left over
                at each distance from this landmark. If being close to it is worth something,
                the near bands come out above the far ones.
              </p>
              <p style={{ margin: 0, color: "#888", fontSize: 13 }}>
                It measures correlation, not cause — a station in a cheaper area will look
                negative without causing anything. Treat it as evidence, not an instruction.
              </p>
            </div>
          )}
          {selected && (
            <>
              <h2 style={{ margin: "0 0 2px", fontSize: 18 }}>{selected.landmark_name}</h2>
              <div style={{ color: "#888", fontSize: 13, marginBottom: 14 }}>
                {selected.shape}{selected.is_imported ? ` · imported from ${selected.source}` : " · added by hand"}
              </div>

              {impactLoading && <div style={{ color: "#666" }}>Measuring…</div>}

              {impact && (
                <>
                  {impact.bands.length === 0 && <div style={{ color: "#666" }}>{impact.note}</div>}
                  {impact.bands.map((b) => (
                    <div key={b.from_m} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                      <span style={{ width: 92, fontSize: 12, color: "#555", fontVariantNumeric: "tabular-nums" }} title="Distance from this landmark">
                        {b.from_m}–{b.to_m || "∞"}m
                      </span>
                      <Bar pct={b.residual_pct} />
                      <span style={{ fontSize: 11, color: "#aaa" }}>n={b.n}</span>
                    </div>
                  ))}

                  {impact.suggested_type && (
                    <div style={{
                      marginTop: 14, padding: 10, borderRadius: 6,
                      background: agrees ? "#f1f8f1" : "#fff8e1",
                      border: `1px solid ${agrees ? "#cfe3cf" : "#ffe0a3"}`,
                    }}>
                      <strong>Suggests {TYPE_LABEL[impact.suggested_type]}</strong>
                      {" "}<span style={{ color: "#666" }}>
                        (spread {impact.spread_pct > 0 ? "+" : ""}{impact.spread_pct}pp across {impact.sample_size.toLocaleString()} leases)
                      </span>
                      {!agrees && <div style={{ fontSize: 13, marginTop: 4 }}>
                        Currently set to <strong>{TYPE_LABEL[impact.current_type]}</strong>.
                      </div>}
                    </div>
                  )}

                  <p style={{ color: "#888", fontSize: 12, marginTop: 12, lineHeight: 1.5 }}>{impact.note}</p>
                </>
              )}

              {edit ? (
                <div style={{ marginTop: 16, padding: 12, background: "#fafafa", border: "1px solid #eee", borderRadius: 6 }}>
                  <input value={edit.landmark_name} placeholder="Name"
                         onChange={(e) => setEdit({ ...edit, landmark_name: e.target.value })}
                         style={{ padding: 8, width: "100%", marginBottom: 8 }} />
                  <input value={edit.description} placeholder="Note (optional)"
                         onChange={(e) => setEdit({ ...edit, description: e.target.value })}
                         style={{ padding: 8, width: "100%", marginBottom: 8 }} />
                  <label style={{ fontSize: 13, color: "#555" }}>
                    Weight&nbsp;
                    <input type="number" step="0.05" min="0" max="3" value={edit.weight}
                           onChange={(e) => setEdit({ ...edit, weight: e.target.value })}
                           style={{ padding: 6, width: 90 }} />
                  </label>
                  <div style={{ marginTop: 10, display: "flex", gap: 8 }}>
                    <button disabled={saving} style={{ padding: "7px 14px" }}
                            onClick={async () => {
                              const ok = await patchLandmark(selected, {
                                landmark_name: edit.landmark_name,
                                description: edit.description,
                                weight: parseFloat(edit.weight) || 1.0,
                              }, "Saved");
                              if (ok) setEdit(null);
                            }}>Save</button>
                    <button onClick={() => setEdit(null)} style={{ padding: "7px 14px" }}>Cancel</button>
                  </div>
                </div>
              ) : (
                <div style={{ marginTop: 16, display: "flex", gap: 10, alignItems: "center", fontSize: 13 }}>
                  <button onClick={() => openQueue(selected)} disabled={saving}
                          style={{ padding: "6px 12px" }}>Review buildings</button>
                  <button onClick={() => setEdit({
                            landmark_name: selected.landmark_name,
                            description: selected.description || "",
                            weight: selected.weight ?? 1.0,
                          })}
                          style={{ padding: "6px 12px" }}>Edit details</button>
                  {!selected.is_imported && selected.shape === "point" && (
                    <span style={{ color: "#888" }}>drag its marker to move it</span>
                  )}
                  {!selected.is_imported ? (
                    <button disabled={saving} onClick={() => removeLandmark(selected)}
                            style={{ padding: "6px 12px", marginLeft: "auto", color: "#c62828", borderColor: "#c62828" }}>
                      Delete
                    </button>
                  ) : (
                    <span style={{ marginLeft: "auto", color: "#999", fontSize: 12 }}>
                      imported — set it Neutral to ignore it
                    </span>
                  )}
                </div>
              )}

              <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
                {["positive", "neutral", "negative"].map((t) => (
                  <button key={t} disabled={saving || selected.landmark_type === t}
                          onClick={() => setType(selected, t)}
                          style={{
                            padding: "8px 14px", borderRadius: 5, cursor: "pointer",
                            border: `1px solid ${TYPE_COLORS[t]}`,
                            background: selected.landmark_type === t ? TYPE_COLORS[t] : "#fff",
                            color: selected.landmark_type === t ? "#fff" : TYPE_COLORS[t],
                            opacity: saving ? 0.6 : 1,
                          }}>
                    {TYPE_LABEL[t]}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {toast && (
        <div onClick={() => setToast(null)}
             style={{ position: "fixed", bottom: 20, left: "50%", transform: "translateX(-50%)",
                      background: "#333", color: "#fff", padding: "10px 18px", borderRadius: 6, cursor: "pointer" }}>
          {toast}
        </div>
      )}
    </div>
  );
}
